import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  APARATOS_SISTEMA,
  ETIQUETA_APARATO_SISTEMA,
  fechaLocalIso,
  sumarDias,
  type AparatoSistema,
  type DictamenAptitud,
  type ResultadoControl,
} from '@ausentia/shared';
import { useSesion } from '../contexto/Sesion.js';
import { actualizarBorrador, borradorActual, enviarBorrador } from './flujo.js';
import { capturarFoto } from './camara.js';

const APTITUD: { v: DictamenAptitud; t: string }[] = [
  { v: 'NO_APTO_TEMPORAL', t: 'Incapacita para trabajar' },
  { v: 'APTO_PARA_TRABAJAR', t: 'No incapacita para trabajar' },
];

const MOTIVOS_EXCEPCION: { v: ResultadoControl; t: string }[] = [
  { v: 'PACIENTE_AUSENTE', t: 'Trabajador ausente' },
  { v: 'DOMICILIO_ERRONEO', t: 'Domicilio no encontrado' },
  { v: 'MEDICO_IMPOSIBILITADO', t: 'Médico no pudo realizarlo' },
  { v: 'RECHAZA_CONTROL', t: 'Trabajador rechaza el control' },
];

export function Control() {
  const navegar = useNavigate();
  const b = borradorActual();
  if (!b)
    return (
      <div className="contenido">
        <p className="muted">No hay un control en curso.</p>
        <button className="primario" onClick={() => navegar('/medico')}>
          Volver a las visitas
        </button>
      </div>
    );
  return <ControlForm />;
}

function ControlForm() {
  const navegar = useNavigate();
  const { medico } = useSesion();
  const b = borradorActual()!;

  // null = sin seleccionar, true = realizado, false = no realizado
  const [realizado, setRealizado] = useState<boolean | null>(null);

  // Branch Sí: dictamen
  const [aptitud, setAptitud] = useState<DictamenAptitud>('NO_APTO_TEMPORAL');
  const [fechaReinsercion, setFechaReinsercion] = useState(
    b.evaluacion?.fechaReinsercion ?? sumarDias(fechaLocalIso(), 1),
  );
  const [aparatoSistema, setAparatoSistema] = useState<AparatoSistema | ''>(
    b.evaluacion?.aparatoSistema ?? '',
  );
  const [observaciones, setObservaciones] = useState(b.evaluacion?.observaciones ?? '');

  // Branch No: excepción
  const [motivo, setMotivo] = useState<ResultadoControl>('PACIENTE_AUSENTE');
  const [observacionesExcepcion, setObservacionesExcepcion] = useState('');
  const [foto, setFoto] = useState<{ id: string; hashSha256: string; capturadaEn: string } | null>(null);

  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function tomarFoto() {
    setError('');
    try {
      const adj = await capturarFoto('FACHADA_FOTO');
      if (adj) setFoto({ id: adj.id, hashSha256: adj.hashSha256, capturadaEn: adj.capturadaEn });
    } catch (err) {
      setError(String(err));
    }
  }

  function continuar() {
    setError('');
    if (aptitud === 'NO_APTO_TEMPORAL') {
      if (!fechaReinsercion) {
        setError('Indicá la fecha de retorno laboral');
        return;
      }
      if (!aparatoSistema) {
        setError('Indicá el aparato o sistema afectado');
        return;
      }
    }
    actualizarBorrador({
      resultado: 'REALIZADA',
      evaluacion: {
        aptitud,
        fechaReinsercion: aptitud === 'APTO_PARA_TRABAJAR' ? null : fechaReinsercion,
        aparatoSistema: aptitud === 'APTO_PARA_TRABAJAR' ? null : (aparatoSistema || null),
        observaciones,
      },
    });
    navegar('/medico/consentimiento');
  }

  async function confirmar() {
    if (observacionesExcepcion.trim().length < 5) {
      setError('Registrá el motivo/observaciones (mínimo 5 caracteres)');
      return;
    }
    setEnviando(true);
    setError('');
    actualizarBorrador({
      resultado: motivo,
      motivoNoRealizada: observacionesExcepcion,
      adjuntosLocales: foto
        ? [{ id: foto.id, tipo: 'FACHADA_FOTO', hashSha256: foto.hashSha256, capturadaEn: foto.capturadaEn }]
        : [],
    });
    try {
      const r = await enviarBorrador(medico!, null);
      if (r.destino === 'firma') navegar('/medico/firma', { state: { codigo: r.codigo, challengeId: r.challengeId, metodo: r.metodoFirma } });
      else if (r.destino === 'bloqueada') navegar('/medico/pendientes', { state: { aviso: r.mensaje } });
      else navegar('/medico/pendientes');
    } catch (err) {
      setError(String(err));
      setEnviando(false);
    }
  }

  return (
    <>
      <div className="barra-titulo">
        <button className="volver" onClick={() => navegar('/medico')}>‹</button>
        <span>Control · {b.orden.trabajador.nombreCompleto}</span>
      </div>
      <div className="contenido">

        <div className="campo">
          <label>¿Se realizó el control?</label>
          <div className="aptitud-toggle">
            <button
              type="button"
              className={`apto-opcion ${realizado === true ? 'activo apto' : ''}`}
              aria-pressed={realizado === true}
              onClick={() => { setRealizado(true); setError(''); }}
            >
              <strong>Sí, se realizó</strong>
            </button>
            <button
              type="button"
              className={`apto-opcion ${realizado === false ? 'activo no-apto' : ''}`}
              aria-pressed={realizado === false}
              onClick={() => { setRealizado(false); setError(''); }}
            >
              <strong>No se realizó</strong>
            </button>
          </div>
        </div>

        {realizado === null && (
          <p className="muted" style={{ textAlign: 'center', padding: '16px 0' }}>
            Todavía no se registró el resultado del control.
          </p>
        )}

        {realizado === true && (
          <>
            <div className="campo">
              <label>Dictamen de aptitud laboral</label>
              <div className="aptitud-toggle">
                {APTITUD.map((a) => (
                  <button
                    key={a.v}
                    type="button"
                    className={`apto-opcion ${aptitud === a.v ? 'activo' : ''} ${a.v === 'APTO_PARA_TRABAJAR' ? 'apto' : 'no-apto'}`}
                    aria-pressed={aptitud === a.v}
                    onClick={() => setAptitud(a.v)}
                  >
                    <strong>{a.t}</strong>
                  </button>
                ))}
              </div>
              <p className="mini">El médico de control no diagnostica: dictamina si el trabajador está apto o no.</p>
            </div>

            {aptitud === 'NO_APTO_TEMPORAL' && (
              <>
                <div className="campo">
                  <label htmlFor="fecha-reinsercion">Fecha de retorno laboral</label>
                  <input
                    id="fecha-reinsercion"
                    type="date"
                    min={fechaLocalIso()}
                    value={fechaReinsercion}
                    onChange={(e) => setFechaReinsercion(e.target.value)}
                  />
                  <p className="mini">Confirma o corrige la fecha del certificado presentado — no es un diagnóstico nuevo.</p>
                </div>

                <div className="campo">
                  <label htmlFor="aparato-sistema">Aparato o sistema afectado</label>
                  <select
                    id="aparato-sistema"
                    value={aparatoSistema}
                    onChange={(e) => setAparatoSistema(e.target.value as AparatoSistema)}
                  >
                    <option value="">-- Seleccionar --</option>
                    {APARATOS_SISTEMA.map((a) => (
                      <option key={a} value={a}>{ETIQUETA_APARATO_SISTEMA[a]}</option>
                    ))}
                  </select>
                  <p className="mini">Clasificación general, no es un diagnóstico — dato laboral, lo ve también RRHH.</p>
                </div>
              </>
            )}

            <div className="campo">
              <label>Observaciones (opcional)</label>
              <textarea rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
              <p className="mini">🔒 Nota reservada — sólo la ve el rol Servicio Médico, no RRHH</p>
            </div>

            {error && <div className="error-box">{error}</div>}
            <button className="primario" onClick={continuar}>Continuar a la devolución</button>
          </>
        )}

        {realizado === false && (
          <>
            <div className="campo">
              <label>Motivo</label>
              <div className="segmentos">
                {MOTIVOS_EXCEPCION.map((m) => (
                  <span
                    key={m.v}
                    className={`seg ${motivo === m.v ? 'activo' : ''}`}
                    onClick={() => setMotivo(m.v)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        setMotivo(m.v);
                      }
                    }}
                  >
                    {m.t}
                  </span>
                ))}
              </div>
            </div>

            <div className="campo">
              <label>Observaciones del profesional</label>
              <textarea
                rows={3}
                value={observacionesExcepcion}
                onChange={(e) => setObservacionesExcepcion(e.target.value)}
                placeholder="Ej.: llamé al timbre tres veces entre 15:30 y 15:50; no respondió nadie."
              />
            </div>

            <div className="campo">
              <label>Evidencia fotográfica (fachada/timbre)</label>
              {foto ? (
                <div className="aviso-info">
                  <span>📷</span>
                  <span>Foto capturada in-app · hash {foto.hashSha256.slice(0, 12)}…</span>
                </div>
              ) : (
                <button className="secundario" onClick={tomarFoto}>📷 Capturar foto (cámara in-app)</button>
              )}
            </div>

            <div className="aviso-info">
              <span>ℹ️</span>
              <span>
                Se firmará digitalmente una constancia de "control no realizado" y se notificará a la empresa.
                La app solo documenta; las consecuencias del art. 210 las evalúa la empresa.
              </span>
            </div>

            {error && <div className="error-box">{error}</div>}
            <button className="primario" onClick={confirmar} disabled={enviando}>
              {enviando ? 'Registrando…' : 'Registrar y firmar'}
            </button>
          </>
        )}

      </div>
    </>
  );
}
